import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Activity,
  ArrowLeft,
  FolderOpen,
  Bot,
  Database,
  GitBranch,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
} from 'lucide-react';
import apiService from '../services/api';
import { useProjectStore } from '../store';

type ActivityRow = {
  activity_log_id: number;
  user_id: number;
  activity_type: string;
  title: string;
  description?: string;
  status: string;
  activity_data?: any;
  created_at: string;
};

const PAGE_SIZE = 50;

const DATE_RANGES: Array<{ label: string; days: number | null }> = [
  { label: 'Last 24 hours', days: 1 },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'All time', days: null },
];

const STATUS_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'All statuses', value: '' },
  { label: 'Success', value: 'success' },
  { label: 'Error', value: 'error' },
  { label: 'Warning', value: 'warning' },
];

const TYPE_GROUPS: Array<{ label: string; types: string[] }> = [
  { label: 'All types', types: [] },
  { label: 'Workflows', types: ['orchestration_run', 'orchestration_failed'] },
  { label: 'Projects', types: ['project_created', 'project_updated', 'project_deleted'] },
  { label: 'Agents', types: ['agent_created', 'agent_updated', 'agent_deleted'] },
  { label: 'Models', types: ['model_created', 'model_updated', 'model_deleted'] },
];

const ActivityHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { projects } = useProjectStore();

  const [activities, setActivities] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const [projectId, setProjectId] = useState<string>('');
  const [daysBack, setDaysBack] = useState<number | null>(7);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeGroup, setTypeGroup] = useState<string>('All types');

  const fetchPage = useCallback(
    async (skip: number, append: boolean) => {
      try {
        if (append) setLoadingMore(true);
        else setLoading(true);
        setError(null);

        const types = TYPE_GROUPS.find(g => g.label === typeGroup)?.types ?? [];

        const response = await apiService.getRecentActivities({
          skip,
          limit: PAGE_SIZE,
          activityTypes: types.length > 0 ? types : undefined,
          statusFilter: statusFilter || undefined,
          daysBack: daysBack ?? undefined,
          projectId: projectId ? Number(projectId) : undefined,
        });

        setActivities(prev => append ? [...prev, ...response.activities] : response.activities);
        setHasMore(response.has_more);
      } catch (err: any) {
        setError(err?.response?.data?.detail || err?.message || 'Failed to load activity');
        if (!append) setActivities([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [projectId, daysBack, statusFilter, typeGroup],
  );

  // Reload when filters change
  useEffect(() => {
    fetchPage(0, false);
  }, [fetchPage]);

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const renderIcon = (activity: ActivityRow) => {
    const activityType = activity.activity_type;
    const isOrchOutcome =
      activityType === 'orchestration_run' || activityType === 'orchestration_failed';
    const isFailure = activity.status === 'error' || activityType === 'orchestration_failed';

    if (isOrchOutcome) {
      const Icon = isFailure ? XCircle : CheckCircle;
      const color = isFailure
        ? 'text-red-600 dark:text-red-400'
        : 'text-emerald-600 dark:text-emerald-400';
      return <Icon className={`w-5 h-5 ${color}`} />;
    }

    let Icon: React.ComponentType<{ className?: string }> = Activity;
    let color = 'text-gray-600 dark:text-gray-400';
    if (activityType.startsWith('project_')) {
      Icon = FolderOpen;
      color = 'text-primary-600';
    } else if (activityType.startsWith('agent_')) {
      Icon = Bot;
      color = 'text-emerald-600 dark:text-emerald-400';
    } else if (activityType.startsWith('model_')) {
      Icon = Database;
      color = 'text-purple-600';
    } else if (activityType.startsWith('orchestration_')) {
      Icon = GitBranch;
      color = 'text-primary-600';
    }
    if (activity.status === 'error') color = 'text-red-600 dark:text-red-400';
    else if (activity.status === 'warning') color = 'text-yellow-600 dark:text-yellow-400';

    return <Icon className={`w-5 h-5 ${color}`} />;
  };

  const titleClass = (activity: ActivityRow) => {
    const isOrchOutcome =
      activity.activity_type === 'orchestration_run' ||
      activity.activity_type === 'orchestration_failed';
    const isFailure =
      activity.status === 'error' || activity.activity_type === 'orchestration_failed';
    if (!isOrchOutcome) return 'text-sm font-medium text-gray-900 dark:text-warm-white';
    return isFailure
      ? 'text-sm font-medium text-red-700 dark:text-red-400'
      : 'text-sm font-medium text-emerald-700 dark:text-emerald-400';
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="mb-4">
        <Link
          to="/dashboard"
          className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Command Center
        </Link>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-warm-white">
          Activity History
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Browse and filter recent activity across your projects.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Project
          </label>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white"
          >
            <option value="">All projects</option>
            {projects.map(p => (
              <option key={p.project_id} value={p.project_id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Date range
          </label>
          <select
            value={daysBack ?? ''}
            onChange={(e) => setDaysBack(e.target.value === '' ? null : Number(e.target.value))}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white"
          >
            {DATE_RANGES.map(r => (
              <option key={r.label} value={r.days ?? ''}>{r.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white"
          >
            {STATUS_OPTIONS.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Type
          </label>
          <select
            value={typeGroup}
            onChange={(e) => setTypeGroup(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white"
          >
            {TYPE_GROUPS.map(g => (
              <option key={g.label} value={g.label}>{g.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Body */}
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow">
        {error && (
          <div className="p-4 text-sm text-red-700 dark:text-red-400 border-b border-red-200 dark:border-red-800">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-6 h-6 text-primary-600 animate-spin mx-auto" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading activity...</p>
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No activity matches the selected filters.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {activities.map(activity => {
              const projectName = activity.activity_data?.project_name as string | undefined;
              const project = projectName ? projects.find(p => p.name === projectName) : null;

              return (
                <li key={activity.activity_log_id} className="p-4 flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">{renderIcon(activity)}</div>
                  <div className="flex-1 min-w-0">
                    <p className={titleClass(activity)}>{activity.title}</p>
                    {projectName && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        in{' '}
                        {project ? (
                          <button
                            onClick={() => navigate(`/projects/${project.project_id}`)}
                            className="font-medium text-primary-600 hover:text-primary-800 hover:underline cursor-pointer"
                          >
                            {projectName}
                          </button>
                        ) : (
                          <span className="font-medium text-gray-500 dark:text-gray-400">
                            {projectName}
                          </span>
                        )}
                      </p>
                    )}
                    {activity.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {activity.description}
                      </p>
                    )}
                    <div className="flex items-center mt-1 text-xs text-gray-500 dark:text-gray-400">
                      <Clock className="w-3 h-3 mr-1" />
                      {formatTime(activity.created_at)}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {!loading && hasMore && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => fetchPage(activities.length, true)}
              disabled={loadingMore}
              className="w-full text-sm text-primary-600 hover:text-primary-800 hover:underline py-2 disabled:opacity-50"
            >
              {loadingMore ? 'Loading...' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityHistoryPage;
